import type { QueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { folderKeys } from '@/hooks/api/useFolders';
import { collaboratorOrgKeys } from '@/hooks/api/useProjectCollaboratorOrgs';
import { projectKeys } from '@/hooks/api/useProjects';
import { CONTEXTS_QUERY_KEY } from '@/lib/context-query-keys';
import { invalidateContextDependentQueries } from '@/lib/invalidate-context-dependent-queries';
import { resolveRouteContextScope } from '@/lib/route-context-scope';
import { projectApi } from '@/services/api/projects';
import { usersApi } from '@/services/api/users';
import {
  getClassicProjectRouteProjectId,
  getParseFlowFocusedProjectId,
  isOnParseFlowPage,
  matchesParseFlowProjectNotification,
  matchesProjectAccessRevocation,
  showParseFlowDeniedRedirectDialog,
} from '@/lib/access-revocation';
import {
  getInvitationCloseOutcome,
  getNotificationMetadataString,
  getNotificationMetadataStringArray,
  isProjectAuthChangedNotification,
  isStoryboardReviewNotification,
} from '@/lib/notification-metadata';
import { filterProjectIdsForAuthChangeSync } from '@/lib/project-auth-change-scope';
import {
  invalidateProjectMemberRelatedQueriesForProjects,
  syncProjectDetailCachesAfterPermissionChange,
} from '@/lib/project-member-cache-invalidation';
import { canAccessParseFlowProject } from '@/lib/project-screenplay-permissions';
import { useAccessRevocationStore } from '@/stores/accessRevocationStore';
import { useAppStore } from '@/stores/appStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { NotificationType, type Notification } from '@/types/notification';
import type { Project } from '@/types/project';
import type { ScreenplayWorkflowSnapshot } from '@/types/ai-session';
import { getScreenplayCoordinationIdentity } from '@/services/api/screenplay-coordination';
import type { MyContexts, OrgItem } from '@/types/user';

export type NotificationHandlerContext = {
  pathname: string;
  search: string;
  activeOrgId: string | null;
  currentProjectId: string | null;
};

export type NotificationSideEffectOptions = {
  skipAccessRevocationDialog?: boolean;
  suppressToast?: boolean;
};

export type ProjectAccessRevokedSideEffectPayload = {
  projectId?: string | null;
  projectName?: string | null;
  affectedProjectIds?: string[];
};

export type ProjectAuthChangedSideEffectPayload = {
  projectId?: string | null;
  projectName?: string | null;
  affectedProjectIds?: string[];
  title?: string | null;
  body?: string | null;
};

type ProjectAuthChangeUiMessage = {
  title?: string | null;
  body?: string | null;
};

function getCachedProjectDetail(queryClient: QueryClient, projectId: string): Project | undefined {
  const entries = queryClient.getQueriesData<Project>({
    predicate: (query) => {
      const key = query.queryKey;
      return key[0] === 'projects' && key[1] === 'detail' && key.includes(projectId);
    },
  });
  return entries.find(([, data]) => data)?.[1];
}

function hasCachedContractorProjectAccess(
  queryClient: QueryClient,
  projectIds: readonly string[]
): boolean {
  return projectIds.some((id) => {
    const project = getCachedProjectDetail(queryClient, id);
    return (
      project?.viewScope === 'external' ||
      project?.collabDirection === 'incoming' ||
      Boolean(project?.activePcoId)
    );
  });
}

async function hasContractorEntryAccess(
  projectId: string,
  activeOrgId: string | null
): Promise<boolean> {
  if (!activeOrgId) return false;
  const entryContext = await projectApi.getEntryContext(projectId);
  return entryContext.options.some(
    (option) => option.kind === 'contractor' && option.orgId === activeOrgId
  );
}

function shouldShowProjectScopedToast(
  context: NotificationHandlerContext,
  relatedProjectIds: string[]
): boolean {
  const routeProjectId = getClassicProjectRouteProjectId(context.pathname);
  if (routeProjectId && relatedProjectIds.includes(routeProjectId)) {
    return true;
  }
  return matchesParseFlowProjectNotification(
    context.pathname,
    context.search,
    relatedProjectIds,
    context.currentProjectId
  );
}

function shouldShowAuthChangeToast(
  context: NotificationHandlerContext,
  relatedProjectIds: string[]
): boolean {
  return shouldShowProjectScopedToast(context, relatedProjectIds);
}

function maybeForceLeaveParseFlowAfterAuthChange(
  queryClient: QueryClient,
  context: NotificationHandlerContext,
  relatedProjectIds: string[]
): void {
  if (!isOnParseFlowPage(context.pathname)) return;

  const focusedProjectId = getParseFlowFocusedProjectId(
    context.pathname,
    context.search,
    context.currentProjectId
  );
  if (!focusedProjectId || !relatedProjectIds.includes(focusedProjectId)) {
    return;
  }

  const project = getCachedProjectDetail(queryClient, focusedProjectId);
  if (!project) return;

  const orgs = useOrgContextStore.getState().orgs;
  const activeOrgId = useOrgContextStore.getState().activeOrgId;
  const projectOrg = project.orgId != null ? orgs.find((org) => org.orgId === project.orgId) : null;
  const currentUserId = useAppStore.getState().user?.userId ?? null;

  const permissionFacts = {
    project,
    currentUserId,
    currentUserOrgRole: projectOrg?.role,
    orgPermissionCodes:
      projectOrg?.orgPermissionCodes ??
      (project.orgId && activeOrgId === project.orgId
        ? orgs.find((org) => org.orgId === project.orgId)?.orgPermissionCodes
        : undefined),
  };

  if (canAccessParseFlowProject(permissionFacts)) return;

  const parentProject = project.parentProjectId
    ? getCachedProjectDetail(queryClient, project.parentProjectId)
    : undefined;
  if (parentProject && canAccessParseFlowProject({ ...permissionFacts, project: parentProject })) {
    return;
  }

  // 已打开的工作流保留为执行态或只读态；具体写入资格由工作流快照决定。
  const identity = getScreenplayCoordinationIdentity();
  const hasWorkflow = queryClient
    .getQueriesData<ScreenplayWorkflowSnapshot>({
      queryKey: ['screenplay-workflow-snapshot'],
      predicate: (query) => query.queryKey[2] === identity,
    })
    .some(
      ([, snapshot]) =>
        snapshot?.projectId === focusedProjectId &&
        (snapshot.coordination?.isOwner || snapshot.workflowId)
    );
  if (hasWorkflow) return;

  showParseFlowDeniedRedirectDialog({
    returnProjectId: focusedProjectId,
    projectName: project.name,
    title: '已无智能分镜权限',
    description:
      '管理员已更新你的项目职能，你当前无法继续使用智能分镜，5 秒后将自动返回项目制作页。',
    affectedProjectIds: relatedProjectIds,
  });
}

function applyProjectAuthChangeUiEffects(
  queryClient: QueryClient,
  notification: ProjectAuthChangeUiMessage,
  context: NotificationHandlerContext,
  relatedProjectIds: string[],
  options?: NotificationSideEffectOptions
): void {
  if (!options?.suppressToast && shouldShowAuthChangeToast(context, relatedProjectIds)) {
    toast({
      title: notification.title?.trim() || '项目协作权限已更新',
      description:
        notification.body?.trim() ||
        '管理员已更新你的项目角色、职能或额外权限组，请按新的协作职责执行。',
      duration: 5000,
    });
  }

  maybeForceLeaveParseFlowAfterAuthChange(queryClient, context, relatedProjectIds);
}

export function applyProjectAccessRevokedEffects(
  queryClient: QueryClient,
  payload: ProjectAccessRevokedSideEffectPayload,
  context: NotificationHandlerContext,
  options?: NotificationSideEffectOptions
): void {
  const projectId = payload.projectId || undefined;
  const affectedProjectIds = payload.affectedProjectIds || [];
  const relatedProjectIds = [projectId, ...affectedProjectIds].filter((id): id is string =>
    Boolean(id)
  );
  invalidateProjectMemberRelatedQueriesForProjects(queryClient, relatedProjectIds);
  const revocationMatchesCurrentRoute = matchesProjectAccessRevocation({
    pathname: context.pathname,
    search: context.search,
    projectId,
    affectedProjectIds,
    fallbackProjectIds: [context.currentProjectId].filter((id): id is string => Boolean(id)),
  });
  const hasContractorAccess = hasCachedContractorProjectAccess(queryClient, relatedProjectIds);
  const showProjectRevocationDialog = () => {
    useAccessRevocationStore.getState().show({
      kind: 'project',
      projectId,
      projectName: payload.projectName || undefined,
      affectedProjectIds,
    });
  };

  if (options?.skipAccessRevocationDialog || !revocationMatchesCurrentRoute) {
    return;
  }

  if (hasContractorAccess) {
    return;
  }

  const entryContextProjectId = projectId || affectedProjectIds[0];
  if (entryContextProjectId) {
    void hasContractorEntryAccess(entryContextProjectId, context.activeOrgId)
      .then((hasEntryAccess) => {
        if (!hasEntryAccess) {
          showProjectRevocationDialog();
        }
      })
      .catch(() => {
        showProjectRevocationDialog();
      });
    return;
  }
  showProjectRevocationDialog();
}

export function applyProjectAuthChangedEffects(
  queryClient: QueryClient,
  payload: ProjectAuthChangedSideEffectPayload,
  context: NotificationHandlerContext,
  options?: NotificationSideEffectOptions
): void {
  const relatedProjectIds = [payload.projectId, ...(payload.affectedProjectIds || [])].filter(
    (id): id is string => Boolean(id)
  );
  const activeOrgId = context.activeOrgId ?? useOrgContextStore.getState().activeOrgId;
  const syncProjectIds = filterProjectIdsForAuthChangeSync(
    relatedProjectIds,
    context,
    queryClient,
    activeOrgId
  );

  if (syncProjectIds.length === 0) {
    applyProjectAuthChangeUiEffects(
      queryClient,
      {
        title: payload.title || undefined,
        body: payload.body || undefined,
      },
      context,
      relatedProjectIds,
      options
    );
    return;
  }

  void Promise.resolve(syncProjectDetailCachesAfterPermissionChange(queryClient, syncProjectIds))
    .then(() => {
      applyProjectAuthChangeUiEffects(
        queryClient,
        {
          title: payload.title || undefined,
          body: payload.body || undefined,
        },
        context,
        relatedProjectIds,
        options
      );
    })
    .catch((error) => {
      console.warn('[notification] 同步项目权限缓存失败', error);
      invalidateProjectMemberRelatedQueriesForProjects(queryClient, syncProjectIds);
      void queryClient.refetchQueries({
        predicate: (query) =>
          query.queryKey[0] === 'projects' &&
          query.queryKey[1] === 'detail' &&
          syncProjectIds.some((id) => query.queryKey.includes(id)),
        type: 'all',
      });
    });
}

const PCO_LIST_REFRESH_NOTIFICATION_TYPES = new Set<NotificationType>([
  NotificationType.PROJECT_ORG_COLLAB_TERMINATED,
  NotificationType.PROJECT_ORG_COLLAB_REVOKED,
]);

/** 承制邀请被撤回或合作被解除时，清掉待处理卡片和承制项目列表。 */
export function applyPcoCollaborationListEffects(
  queryClient: QueryClient,
  notification: Pick<Notification, 'type' | 'metadata'>
): void {
  const withdrawnInvite =
    notification.type === NotificationType.PROJECT_ORG_COLLAB_INVITED &&
    getInvitationCloseOutcome(notification.metadata) === 'cancelled';
  if (!withdrawnInvite && !PCO_LIST_REFRESH_NOTIFICATION_TYPES.has(notification.type)) return;
  void queryClient.invalidateQueries({ queryKey: collaboratorOrgKeys.all });
  void queryClient.invalidateQueries({ queryKey: folderKeys.trees() });
  void queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
}

export function handleNotificationToast(
  notification: Notification,
  context: NotificationHandlerContext,
  options?: NotificationSideEffectOptions
): void {
  if (options?.suppressToast) return;

  const projectId = getNotificationMetadataString(notification.metadata, 'projectId', 'project_id');
  const affectedProjectIds = getNotificationMetadataStringArray(
    notification.metadata,
    'affectedProjectIds',
    'affected_project_ids'
  );

  if (isProjectAuthChangedNotification(notification.type)) {
    const relatedProjectIds = [projectId, ...affectedProjectIds].filter((id): id is string =>
      Boolean(id)
    );
    if (!shouldShowAuthChangeToast(context, relatedProjectIds)) return;
    toast({
      title: notification.title?.trim() || '项目协作权限已更新',
      description:
        notification.body?.trim() ||
        '管理员已更新你的项目角色、职能或额外权限组，请按新的协作职责执行。',
      duration: 5000,
    });
    return;
  }

  if (isStoryboardReviewNotification(notification.type) && projectId) {
    if (!shouldShowProjectScopedToast(context, [projectId])) return;
    toast({
      title: notification.title?.trim() || '分镜审核状态已更新',
      description: notification.body?.trim() || undefined,
      duration: 5000,
    });
    return;
  }

  if (notification.type === NotificationType.SCREENPLAY_PARSE_COMPLETED && projectId) {
    const screenplayProjectId = getNotificationMetadataString(
      notification.metadata,
      'screenplayProjectId',
      'screenplay_project_id'
    );
    const parentProjectId = getNotificationMetadataString(
      notification.metadata,
      'parentProjectId',
      'parent_project_id'
    );
    const toastProjectIds = [projectId, screenplayProjectId, parentProjectId].filter(
      (id): id is string => Boolean(id)
    );
    if (!shouldShowProjectScopedToast(context, toastProjectIds)) return;
    toast({
      title: notification.title?.trim() || '剧本解析已完成',
      description: notification.body?.trim() || undefined,
      duration: 5000,
    });
  }
}

export type OrgAccessChangedSideEffectPayload = {
  orgId?: string | null;
  orgName?: string | null;
};

type OrgMembershipRedirectListener = (path: string) => void;

const orgAccessChangedInflight = new Map<string, Promise<void>>();
let orgMembershipRedirectListener: OrgMembershipRedirectListener | null = null;

export function subscribeOrgMembershipRedirect(
  listener: OrgMembershipRedirectListener
): () => void {
  orgMembershipRedirectListener = listener;
  return () => {
    if (orgMembershipRedirectListener === listener) {
      orgMembershipRedirectListener = null;
    }
  };
}

function requestOrgMembershipRedirect(path: string): void {
  orgMembershipRedirectListener?.(path);
}

export function isUsingOrgIdentity(
  context: Pick<NotificationHandlerContext, 'pathname' | 'activeOrgId'>,
  orgId: string
): boolean {
  if (context.activeOrgId === orgId) return true;
  const scope = resolveRouteContextScope(context.pathname);
  return scope.type === 'org' && scope.orgId === orgId;
}

/** 离开某个团队身份后，把当前组织路由改写成对应的个人路由。 */
export function personalPathAfterLeavingOrg(
  pathname: string,
  search: string,
  orgId: string
): string | null {
  const scope = resolveRouteContextScope(pathname);
  if (scope.type === 'personal') return null;
  if (scope.type === 'org' && scope.orgId !== orgId) return null;

  const query = search && search !== '?' ? (search.startsWith('?') ? search : `?${search}`) : '';
  if (scope.type === 'unknown') return '/workspace';

  const [root, second, third] = pathname.split('/').filter(Boolean);
  if (second !== orgId) return '/workspace';
  if (root === 'workspace') {
    return third ? `/workspace/f/${third}${query}` : `/workspace${query}`;
  }
  if (root === 'collab') {
    return third ? `/collab/f/${third}${query}` : `/collab${query}`;
  }
  if (root === 'trash') return `/trash${query}`;
  if (root === 'shared') return `/shared${query}`;
  if (root === 'ai-toolbox') {
    return third ? `/ai-toolbox/${third}${query}` : `/ai-toolbox${query}`;
  }
  if (root === 'org') return '/workspace';
  return '/workspace';
}

function listContextOrgs(contexts: MyContexts): OrgItem[] {
  return [...contexts.enterprises, ...contexts.teams];
}

function orgIdentityLabel(name: string | null | undefined): string {
  const trimmed = name?.trim();
  return trimmed ? `「${trimmed}」` : '该团队';
}

async function runOrgAccessChangedEffects(
  queryClient: QueryClient,
  payload: OrgAccessChangedSideEffectPayload,
  context: NotificationHandlerContext
): Promise<void> {
  const previousOrgs = useOrgContextStore.getState().orgs;
  let contexts: MyContexts;
  try {
    contexts = await usersApi.getMyContexts();
  } catch (error) {
    console.warn('[notification] 刷新可切换身份失败', error);
    return;
  }
  if (!contexts?.enterprises || !contexts.teams) return;

  queryClient.setQueryData(CONTEXTS_QUERY_KEY, contexts);
  const nextOrgs = listContextOrgs(contexts);
  useOrgContextStore.getState().setOrgs(contexts.enterprises, contexts.teams);

  const previousIds = new Set(previousOrgs.map((org) => org.orgId));
  const nextIds = new Set(nextOrgs.map((org) => org.orgId));
  const focusOrgId = payload.orgId || null;
  const added = nextOrgs.filter((org) => !previousIds.has(org.orgId));
  const removed = previousOrgs.filter((org) => !nextIds.has(org.orgId));
  const focusedAdded = focusOrgId ? added.filter((org) => org.orgId === focusOrgId) : added;
  const focusedRemoved = focusOrgId ? removed.filter((org) => org.orgId === focusOrgId) : removed;

  for (const org of focusedAdded) {
    toast({
      title: `已加入${orgIdentityLabel(org.name || payload.orgName)}`,
      description: '可在身份切换中选择该团队。',
      duration: 5000,
    });
  }

  const removedCurrent =
    focusedRemoved.find((org) => isUsingOrgIdentity(context, org.orgId)) ??
    (focusOrgId && !nextIds.has(focusOrgId) && isUsingOrgIdentity(context, focusOrgId)
      ? { orgId: focusOrgId, name: payload.orgName ?? '' }
      : null);
  if (removedCurrent) {
    toast({
      title: `已离开${orgIdentityLabel(removedCurrent.name || payload.orgName)}`,
      description: '当前身份已切换为个人空间。',
      duration: 5000,
    });
    useOrgContextStore.getState().setActiveOrg(null, 0, { broadcast: false });
    try {
      await usersApi.switchContext({ contextType: 'personal' });
    } catch (error) {
      console.warn('[notification] 切换到个人身份失败', error);
    }
    const nextPath = personalPathAfterLeavingOrg(
      context.pathname,
      context.search,
      removedCurrent.orgId
    );
    if (nextPath) requestOrgMembershipRedirect(nextPath);
    invalidateContextDependentQueries(queryClient);
    return;
  }

  if (focusedRemoved.length > 0) {
    toast({
      title: `已离开${orgIdentityLabel(focusedRemoved[0]?.name || payload.orgName)}`,
      description: '该团队已从身份切换中移除。',
      duration: 5000,
    });
  }
}

export function applyOrgAccessChangedEffects(
  queryClient: QueryClient,
  payload: OrgAccessChangedSideEffectPayload,
  context: NotificationHandlerContext
): Promise<void> {
  const key = payload.orgId || '*';
  const current = orgAccessChangedInflight.get(key);
  if (current) return current;

  const task = runOrgAccessChangedEffects(queryClient, payload, context).finally(() => {
    if (orgAccessChangedInflight.get(key) === task) {
      orgAccessChangedInflight.delete(key);
    }
  });
  orgAccessChangedInflight.set(key, task);
  return task;
}
